import AppLayout from '@/components/layout/AppLayout';
import { Suspense, lazy } from 'react';
import { useMemo } from 'react';
import { useDashboardTours } from '@/tours/dashboardTours';

const MapDemoPage = lazy(() => import('@/views/map-demo/MapDemoPage'));
const SettingsPage = lazy(() => import('@/views/settings/SettingsPage'));
const MapGuidePage = lazy(() => import('@/views/maps/MapGuidePage'));
const MapSourcesPage = lazy(() => import('@/views/maps/MapSourcesPage'));
const MapBasemapTilesPage = lazy(() => import('@/views/maps/MapBasemapTilesPage'));
const MapStylesPage = lazy(() => import('@/views/maps/MapStylesPage'));
const MapCustomMapsPage = lazy(() => import('@/views/maps/MapCustomMapsPage'));
const MapSpriteSetsPage = lazy(() => import('@/views/maps/MapSpriteSetsPage'));
const MapGlyphSetsPage = lazy(() => import('@/views/maps/MapGlyphSetsPage'));
const MapStyleWorkbenchPage = lazy(() => import('@/views/maps/MapStyleWorkbenchPage'));
const MapCustomMapViewerPage = lazy(() => import('@/views/maps/MapCustomMapViewerPage'));
const UsersPage = lazy(() => import('@/views/admin/UsersPage'));
const UserDetailPage = lazy(() => import('@/views/admin/UserDetailPage'));
const TenantsPage = lazy(() => import('@/views/admin/TenantsPage'));
const TenantApplicationsPage = lazy(() => import('@/views/admin/TenantApplicationsPage'));
const TenantDetailPage = lazy(() => import('@/views/admin/TenantDetailPage'));
const TenantUserManagementPage = lazy(() => import('@/views/admin/TenantUserManagementPage'));
const AuditPage = lazy(() => import('@/views/admin/AuditPage'));
const ApiGatewayPage = lazy(() => import('@/views/admin/ApiGatewayPage'));
const FeatureFlagsPage = lazy(() => import('@/views/admin/FeatureFlagsPage'));
const ResolversPage = lazy(() => import('@/views/admin/ResolversPage'));
const AccountLinksPage = lazy(() => import('@/views/admin/AccountLinksPage'));
const AccountDetailPage = lazy(() => import('@/views/admin/AccountDetailPage'));
const SearchSyncStatus = lazy(() => import('@/views/admin/SearchSyncStatus'));
const SessionDebugPage = lazy(() => import('@/views/admin/SessionDebugPage'));
const PlatformHealthPage = lazy(() => import('@/views/ops/PlatformHealthPage'));
const OpenStackProjectsPage = lazy(() => import('@/views/openstack/OpenStackProjectsPage'));
const OpenStackKeystoneProjectsPage = lazy(() => import('@/views/openstack/OpenStackKeystoneProjectsPage'));
const OpenStackSubstratesPage = lazy(() => import('@/views/openstack/OpenStackSubstratesPage'));
const OpenStackDeploymentsPage = lazy(() => import('@/views/openstack/OpenStackDeploymentsPage'));
const OpenStackOperationsPage = lazy(() => import('@/views/openstack/OpenStackOperationsPage'));

// Coverage Data Management
const CoverageDataUploadPage = lazy(() => import('@/pages/CoverageDataUpload').then(m => ({ default: m.CoverageDataUpload })));

const RouteFallback = () => (
  <div className="p-6 text-sm text-muted-foreground">Loading view…</div>
);

function mapLegacyAccessScopeToTab(scope: string | null): string {
  if (scope === 'platform') return 'platform';
  if (scope === 'organization' || scope === 'organizations') return 'organizations';
  if (scope === 'tenant' || scope === 'tenants') return 'tenants';
  if (scope === 'account' || scope === 'accounts') return 'accounts';
  if (scope === 'effective-permissions') return 'effective-permissions';
  if (scope === 'permission-catalog') return 'permission-catalog';
  return 'tenants';
}

export function isKnownAppRoute(path: string): boolean {
  if (path.startsWith('/maps/custom-maps/') && path.endsWith('/workbench')) return true;
  if (path.startsWith('/maps/custom-maps/') && !path.endsWith('/workbench')) return true;
  if (path === '/maps/custom-maps') return true;
  if (path === '/maps/custom-maps/workbench') return true;
  if (path.startsWith('/maps/styles/') && path.endsWith('/workbench')) return true;
  if (path.startsWith('/maps/workbench')) return true;
  if (path === '/maps/guide') return true;
  if (path === '/maps/basemaps') return true;
  if (path === '/maps/sources') return true;
  if (path === '/maps/styles') return true;
  if (path === '/maps/sprites') return true;
  if (path === '/maps/glyphs') return true;
  if (path === '/map-demo') return true;
  if (path === '/settings') return true;
  if (path === '/admin/users') return true;
  if (path.startsWith('/admin/users/') && path.endsWith('/access')) return true;
  if (path.startsWith('/admin/users/')) return true;
  if (path === '/admin/tenants') return true;
  if (path === '/admin/tenant-applications') return true;
  if (path.startsWith('/admin/tenants/') && path.endsWith('/users')) return true;
  if (path.startsWith('/admin/tenants/') && path.endsWith('/telemetry-backends')) return true;
  if (path.startsWith('/admin/tenants/')) return true;
  if (path === '/admin/audit') return true;
  if (path === '/admin/api-gateway') return true;
  if (path === '/admin/api-gateway/keys') return true;
  if (path === '/admin/feature-flags') return true;
  if (path === '/admin/resolvers') return true;
  if (path === '/admin/accounts') return true;
  if (path === '/admin/accounts/link') return false;
  if (path.startsWith('/admin/accounts/')) return true;
  if (path === '/debug/session') return true;
  if (path === '/admin/search-sync') return true;
  if (path === '/admin/coverage-upload') return true;
  if (path === '/ops/platform-health') return true;
  if (path.startsWith('/ops/platform-health/')) return true;
  if (path === '/openstack/projects') return true;
  if (path === '/openstack/keystone-projects') return true;
  if (path === '/openstack/substrates') return true;
  if (path === '/openstack/deployments') return true;
  if (path === '/openstack/operations') return true;
  return false;
}

function resolveRoute(path: string, searchParams: URLSearchParams): JSX.Element {
  if (path.startsWith('/maps/custom-maps/') && path.endsWith('/workbench')) {
    const parts = path.split('/');
    const styleId = parts[3] && parts[3] !== 'workbench' ? decodeURIComponent(parts[3]) : undefined;
    const mode = searchParams.get('mode');
    if (mode === 'view' || mode === 'customize-save-copy') {
      return <MapCustomMapViewerPage styleId={styleId} mode={mode} />;
    }
    return <MapStyleWorkbenchPage styleId={styleId} assetMode="custom-map" />;
  }
  if (path.startsWith('/maps/custom-maps/') && !path.endsWith('/workbench')) {
    const parts = path.split('/');
    const styleId = parts[3] ? decodeURIComponent(parts[3]) : undefined;
    return <MapCustomMapViewerPage styleId={styleId} mode={searchParams.get('mode')} />;
  }
  if (path === '/maps/custom-maps/workbench') {
    return <MapStyleWorkbenchPage assetMode="custom-map" />;
  }
  if (path === '/maps/custom-maps') {
    return <MapCustomMapsPage />;
  }
  if (path.startsWith('/maps/styles/') && path.endsWith('/workbench')) {
    const parts = path.split('/');
    const styleId = parts[3] && parts[3] !== 'workbench' ? decodeURIComponent(parts[3]) : undefined;
    return <MapStyleWorkbenchPage styleId={styleId} />;
  }
  if (path.startsWith('/maps/workbench')) {
    return <MapStyleWorkbenchPage />;
  }
  if (path === '/maps/guide') {
    return <MapGuidePage />;
  }
  if (path === '/maps/basemaps') return <MapBasemapTilesPage />;
  if (path === '/maps/sources') return <MapSourcesPage />;
  if (path === '/maps/styles') return <MapStylesPage />;
  if (path === '/maps/sprites') return <MapSpriteSetsPage />;
  if (path === '/maps/glyphs') return <MapGlyphSetsPage />;
  if (path === '/map-demo') return <MapDemoPage />;
  if (path === '/settings') return <SettingsPage />;
  if (path === '/admin/users') return <UsersPage />;
  if (path.startsWith('/admin/users/') && path.endsWith('/access')) {
    const parts = path.split('/');
    const userId = decodeURIComponent(parts[3] ?? '');
    const mappedTab = mapLegacyAccessScopeToTab(searchParams.get('scope'));
    return <UserDetailPage userId={userId} initialTab={mappedTab} legacyAccessAlias={true} />;
  }
  if (path.startsWith('/admin/users/')) {
    const userId = decodeURIComponent(path.split('/').pop() ?? '');
    const requestedTab = searchParams.get('tab');
    const initialTab = requestedTab === 'access' ? mapLegacyAccessScopeToTab(searchParams.get('scope')) : requestedTab ?? undefined;
    return <UserDetailPage userId={userId} initialTab={initialTab} />;
  }
  if (path === '/admin/tenants') return <TenantsPage />;
  if (path === '/admin/tenant-applications') return <TenantApplicationsPage />;
  if (path.startsWith('/admin/tenants/') && path.endsWith('/users')) {
    const parts = path.split('/');
    const tenantId = decodeURIComponent(parts[3] ?? '');
    return <TenantUserManagementPage tenantId={tenantId} />;
  }
  if (path.startsWith('/admin/tenants/') && path.endsWith('/telemetry-backends')) {
    const parts = path.split('/');
    const tenantId = decodeURIComponent(parts[3] ?? '');
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('tenants.detail.activeTab', 'telemetry');
    }
    return <TenantDetailPage tenantId={tenantId} />;
  }
  if (path.startsWith('/admin/tenants/')) {
    const tenantId = decodeURIComponent(path.split('/').pop() ?? '');
    return <TenantDetailPage tenantId={tenantId} />;
  }
  if (path === '/admin/audit') return <AuditPage />;
  if (path === '/admin/api-gateway' || path === '/admin/api-gateway/keys') return <ApiGatewayPage />;
  if (path === '/admin/feature-flags') return <FeatureFlagsPage />;
  if (path === '/admin/resolvers') return <ResolversPage />;
  if (path === '/admin/accounts') return <AccountLinksPage />;
  if (path.startsWith('/admin/accounts/')) {
    const accountId = decodeURIComponent(path.split('/').pop() ?? '');
    return <AccountDetailPage accountId={accountId} />;
  }
  if (path === '/debug/session') return <SessionDebugPage />;
  if (path === '/admin/search-sync') return <SearchSyncStatus />;
  if (path === '/admin/coverage-upload') return <CoverageDataUploadPage />;
  if (path === '/ops/platform-health') return <PlatformHealthPage initialRange={searchParams.get('range') ?? undefined} />;
  if (path.startsWith('/ops/platform-health/')) {
    const serviceId = decodeURIComponent(path.split('/').pop() ?? '');
    return <PlatformHealthPage serviceId={serviceId} initialRange={searchParams.get('range') ?? undefined} />;
  }
  if (path === '/openstack/projects') return <OpenStackProjectsPage />;
  if (path === '/openstack/keystone-projects') return <OpenStackKeystoneProjectsPage />;
  if (path === '/openstack/substrates') return <OpenStackSubstratesPage />;
  if (path === '/openstack/deployments') return <OpenStackDeploymentsPage />;
  if (path === '/openstack/operations') return <OpenStackOperationsPage />;

  return <UsersPage />;
}

export default function AppRoutes({ path }: { path: string }) {
  const cleanPath = useMemo(() => path.split('?')[0], [path]);
  const searchParams = useMemo(() => {
    const search = path.includes('?') ? path.slice(path.indexOf('?') + 1) : '';
    return new URLSearchParams(search);
  }, [path]);
  const content = resolveRoute(cleanPath, searchParams);
  useDashboardTours();
  return (
    <AppLayout path={cleanPath}>
      <Suspense fallback={<RouteFallback />}>{content}</Suspense>
    </AppLayout>
  );
}
